import { ChartData } from 'chart.js';
import { useEffect, useState } from 'react';

import { GpteEvent, GpteEventRequest } from '../../../../../admin-center/gen/h2ogpte/h2ogpte_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { formatDateLocal, NestedRawChartData, transformNestedDataForCharts } from '../../../common/utils';
import { gpteEventCountsColorMap, gpteEventCountsLabelsMap, H2OGPTeEntry, mapActionName } from '../H2OGPTe.utils';

export const useGPTeEvents = (startDate: Date, endDate: Date, resource?: string, action?: string) => {
  const service = useAdminCenterService();
  const [gpteEvents, setGpteEvents] = useState<H2OGPTeEntry[] | null>(null);
  const [eventsLoading, setEventsLoading] = useState<boolean>(true);
  const [eventsError, setEventsError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGPTeEvents = async () => {
      setEventsLoading(true);
      try {
        const request: GpteEventRequest = {
          startDate: formatDateLocal(startDate),
          endDate: formatDateLocal(endDate),
          resource: resource === undefined || resource === 'all' ? undefined : resource,
          action: action === undefined || action === 'all' ? undefined : action,
        };

        const response = await service.getGpteEvents(request);

        const transformedEvents: H2OGPTeEntry[] =
          response.events?.map((event: GpteEvent) => {
            return {
              date: event.date ? formatDateLocal(new Date(event.date)) : '-',
              username: event.userName ?? 'unknown',
              resource: event.resource as
                | 'Chat'
                | 'API Called'
                | 'RPC Called'
                | 'Collection'
                | 'Job'
                | 'Document'
                | 'Other'
                | 'N/A',
              action: mapActionName(event.action ?? '-'),
              count: event.count ?? 0,
            };
          }) ?? [];

        setGpteEvents(transformedEvents);
      } catch (error) {
        setEventsError('Error fetching login events');
      } finally {
        setEventsLoading(false);
      }
    };

    fetchGPTeEvents();
  }, [startDate, endDate, resource, action, service]);

  return { gpteEvents, eventsLoading, eventsError };
};

export const useGpteEventCounts = (startDate: Date, endDate: Date) => {
  const service = useAdminCenterService();
  const [gpteEventCountsChartData, setGpteEventCountsChartData] = useState<ChartData<'line'> | null>(null);
  const [eventCountsLoading, setEventCountsLoading] = useState<boolean>(true);
  const [eventCountsError, setEventCountsError] = useState<string | null>(null);

  useEffect(() => {
    const fetchExecutiveViewOverallStats = async () => {
      try {
        setEventCountsLoading(true);
        const request = {
          startDate: formatDateLocal(startDate),
          endDate: formatDateLocal(endDate),
        };
        const gpteEventCounts = await service.getGpteEventCounts(request);
        const transformedGPTelEventCounts = transformNestedDataForCharts(
          gpteEventCounts.data as NestedRawChartData,
          gpteEventCountsLabelsMap,
          gpteEventCountsColorMap
        );
        setGpteEventCountsChartData(transformedGPTelEventCounts as ChartData<'line'>);
      } catch (error) {
        setEventCountsError('Error fetching gpte event counts data');
      } finally {
        setEventCountsLoading(false);
      }
    };
    fetchExecutiveViewOverallStats();
  }, [startDate, endDate, service]);

  return { gpteEventCountsChartData, eventCountsLoading, eventCountsError };
};
